import { useEffect, useRef } from 'react';

const SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

const getFocusableElements = (container) => {
  if (!container) return [];
  return Array.from(container.querySelectorAll(SELECTOR))
    .filter((element) => {
      const hidden = element.getAttribute('aria-hidden') === 'true';
      const disabled = element.hasAttribute('disabled');
      const style = window.getComputedStyle(element);
      return !hidden && !disabled && style.display !== 'none' && style.visibility !== 'hidden';
    });
};

const getPreferredInitialTarget = (container, initialFocusRef) => {
  const explicitTarget = initialFocusRef?.current;
  if (explicitTarget) return explicitTarget;

  const preferredSelector = [
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[contenteditable="true"]'
  ].join(',');

  const preferredTarget = container?.querySelector(preferredSelector);
  if (preferredTarget) return preferredTarget;

  const focusable = getFocusableElements(container);
  return focusable[0] || container;
};

export const useFocusTrap = ({ active, onEscape, initialFocusRef } = {}) => {
  const containerRef = useRef(null);
  const escapeHandlerRef = useRef(onEscape);
  const wasActiveRef = useRef(false);

  useEffect(() => {
    escapeHandlerRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    if (!active || !containerRef.current) return undefined;

    const container = containerRef.current;
    const shouldMoveInitialFocus = !wasActiveRef.current;
    wasActiveRef.current = true;
    const previousActiveElement = document.activeElement;
    const initialTarget = getPreferredInitialTarget(container, initialFocusRef);

    if (!container.hasAttribute('tabindex')) {
      container.setAttribute('tabindex', '-1');
    }

    if (shouldMoveInitialFocus) {
      window.setTimeout(() => {
        initialTarget?.focus?.();
      }, 0);
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        escapeHandlerRef.current?.(event);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(container);
      if (focusableElements.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];
      const activeElement = document.activeElement;

      if (event.shiftKey && (activeElement === first || activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElement?.focus) {
        previousActiveElement.focus();
      }
    };
  }, [active, initialFocusRef]);

  useEffect(() => {
    if (active) return;
    wasActiveRef.current = false;
  }, [active]);

  return containerRef;
};
